import { resolveCurrentClientShopSlug } from '~/utils/resolveShopSlug'
import { readShopStorage, removeShopStorage, writeShopStorage } from '~/utils/shopStorage'

export const COOKIE_CONSENT_VERSION = '1'
export const COOKIE_CONSENT_MAX_AGE_DAYS = 180

const STORAGE_SUFFIX = 'cookie_consent'
const LEGACY_GLOBAL_KEY = 'cdt_cookie_consent'

export interface CookieConsentPreferences {
  version: string
  necessary: true
  analytics: boolean
  marketing: boolean
  decidedAt: string
}

export interface CookieCategoryDefinition {
  id: 'necessary' | 'analytics' | 'marketing'
  label: string
  description: string
  required: boolean
}

export const COOKIE_CATEGORIES: CookieCategoryDefinition[] = [
  {
    id: 'necessary',
    label: 'Strict necesare',
    description: 'Necesare pentru funcționarea site-ului: coș, autentificare, securitate și memorarea preferințelor de confidențialitate.',
    required: true,
  },
  {
    id: 'analytics',
    label: 'Analiză și performanță',
    description: 'Ne ajută să înțelegem cum este folosit site-ul (ex. Google Analytics). Nu sunt activate implicit.',
    required: false,
  },
  {
    id: 'marketing',
    label: 'Marketing și publicitate',
    description: 'Permit măsurarea campaniilor și afișarea de reclame relevante (ex. Meta Pixel). Nu sunt activate implicit.',
    required: false,
  },
]

function resolveSlug(slug?: string): string {
  return slug?.trim().toLowerCase() || resolveCurrentClientShopSlug()
}

function isValidConsent(value: unknown): value is CookieConsentPreferences {
  if (!value || typeof value !== 'object') return false
  const record = value as Partial<CookieConsentPreferences>
  return record.version === COOKIE_CONSENT_VERSION
    && record.necessary === true
    && typeof record.analytics === 'boolean'
    && typeof record.marketing === 'boolean'
    && typeof record.decidedAt === 'string'
}

export function isConsentExpired(preferences: CookieConsentPreferences): boolean {
  const decidedAt = Date.parse(preferences.decidedAt)
  if (!Number.isFinite(decidedAt)) return true
  const maxAgeMs = COOKIE_CONSENT_MAX_AGE_DAYS * 24 * 60 * 60 * 1000
  return Date.now() - decidedAt > maxAgeMs
}

function parseConsent(raw: string | null): CookieConsentPreferences | null {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as unknown
    if (!isValidConsent(parsed)) return null
    if (isConsentExpired(parsed)) return null
    return parsed
  } catch {
    return null
  }
}

function readLegacyGlobalConsent(): string | null {
  if (!import.meta.client) return null
  return localStorage.getItem(LEGACY_GLOBAL_KEY)
}

export function readCookieConsent(slug?: string): CookieConsentPreferences | null {
  if (!import.meta.client) return null

  const shopSlug = resolveSlug(slug)
  let raw = shopSlug ? readShopStorage(shopSlug, STORAGE_SUFFIX) : null

  if (!raw) {
    const legacy = readLegacyGlobalConsent()
    if (legacy && shopSlug) {
      writeShopStorage(shopSlug, STORAGE_SUFFIX, legacy)
      localStorage.removeItem(LEGACY_GLOBAL_KEY)
      raw = legacy
    } else {
      raw = legacy
    }
  }

  return parseConsent(raw)
}

export function writeCookieConsent(preferences: CookieConsentPreferences, slug?: string): void {
  if (!import.meta.client) return
  const shopSlug = resolveSlug(slug)
  if (!shopSlug) return
  writeShopStorage(shopSlug, STORAGE_SUFFIX, JSON.stringify(preferences))
  localStorage.removeItem(LEGACY_GLOBAL_KEY)
}

export function clearCookieConsent(slug?: string): void {
  if (!import.meta.client) return
  const shopSlug = resolveSlug(slug)
  if (shopSlug) {
    removeShopStorage(shopSlug, STORAGE_SUFFIX)
  }
  localStorage.removeItem(LEGACY_GLOBAL_KEY)
}

export function buildCookieConsent(
  analytics: boolean,
  marketing: boolean,
): CookieConsentPreferences {
  return {
    version: COOKIE_CONSENT_VERSION,
    necessary: true,
    analytics,
    marketing,
    decidedAt: new Date().toISOString(),
  }
}
